import React, { useState, useEffect } from "react";
import { FileText, Package } from "lucide-react";
import { DocumentParser, type DocumentData } from "../file-viewer/xml-document-parser.service";

interface XmlPreviewProps {
	url: string;
	width?: number;
}

export const XmlPreview: React.FC<XmlPreviewProps> = ({ url, width }) => {
	const [documentData, setDocumentData] = useState<DocumentData | null>(null);
	const [error, setError] = useState<string>("");
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchXml = async () => {
			try {
				setLoading(true);
				const response = await fetch(url);
				const text = await response.text();
				
				const parsed = DocumentParser.parseXmlDocument(text);
				setDocumentData(parsed);
				setError("");
			} catch (err) {
				setError("Fehler beim Laden der XML-Datei");
				console.error("Error loading XML:", err);
			} finally {
				setLoading(false);
			}
		};

		fetchXml();
	}, [url]);
	
	const getDocumentIcon = () => {
		if (!documentData) return <FileText className="h-5 w-5" />;
		return documentData.type === 'lieferschein' ? 
			<Package className="h-5 w-5" /> : 
			<FileText className="h-5 w-5" />;
	};
	
	const getDocumentTypeLabel = () => {
		if (!documentData) return 'XML Dokument';
		switch (documentData.type) {
			case 'lieferschein':
				return 'Lieferschein';
			case 'kostenvoranschlag':
				return 'Kostenvoranschlag';
			default:
				return 'XML Dokument';
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-full bg-gray-50">
				<div className="text-gray-500 text-sm">Wird geladen...</div>
			</div>
		);
	}

	if (error || !documentData) {
		return (
			<div className="flex items-center justify-center h-full bg-gray-50">
				<div className="text-center p-4">
					<FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
					<div className="text-sm text-gray-600">XML Dokument</div>
					{error && <div className="text-xs text-red-500 mt-1">{error}</div>}
				</div>
			</div>
		);
	}

	// For very small thumbnails (< 100px), show minimal version
	const isSmallThumbnail = width && width < 100;
	
	if (isSmallThumbnail) {
		return (
			<div className={`h-full w-full flex items-center justify-center ${
				documentData.type === 'lieferschein' ? 'bg-blue-100' : 'bg-orange-100'
			}`}>
				<div className="text-center">
					<div className="text-[12px] font-bold text-gray-700">
						{documentData.type === 'lieferschein' ? 'LS' : 'KV'} XML
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="h-full w-full flex items-center justify-center bg-white p-4">
			<div className="text-center">
				<div className={`inline-flex p-3 rounded-lg mb-2 ${
					documentData.type === 'lieferschein' ? 'bg-blue-50' : 'bg-orange-50'
				}`}>
					{getDocumentIcon()}
				</div>
				<div className="font-medium text-gray-900">
					{getDocumentTypeLabel()} XML
				</div>
				<div className="text-sm text-gray-600 mt-1">
					Nr: {documentData.documentNumber}
				</div>
				{documentData.date && (
					<div className="text-sm text-gray-500">
						{documentData.date}
					</div>
				)}
			</div>
		</div>
	);
};